'use client'

import * as React from 'react'

import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

import { usePromptInput } from './PromptInputContext'

interface PromptInputTextareaProps
  extends React.ComponentPropsWithoutRef<typeof Textarea> {
  disableAutosize?: boolean
}

export const PromptInputTextarea = React.forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
  ({ className, onKeyDown, disableAutosize = false, placeholder, ...props }, ref) => {
    const { value, setValue, maxHeight, onSubmit, disabled } = usePromptInput()
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)

    React.useEffect(() => {
      if (disableAutosize || !textareaRef.current) return
      const textarea = textareaRef.current
      textarea.style.height = 'auto'
      textarea.style.height =
        typeof maxHeight === 'number'
          ? `${Math.min(textarea.scrollHeight, maxHeight)}px`
          : `min(${textarea.scrollHeight}px, ${maxHeight})`
    }, [disableAutosize, maxHeight, value])

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        onSubmit?.()
      }
      onKeyDown?.(event)
    }

    return (
      <Textarea
        ref={(element) => {
          textareaRef.current = element
          if (typeof ref === 'function') {
            ref(element)
          } else if (ref) {
            ;(ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = element
          }
        }}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        className={cn('text-base text-black font-medium border-0 focus:ring-0 focus:outline-none', className)}
        disabled={disabled}
        placeholder={placeholder}
        {...props}
      />
    )
  }
)

PromptInputTextarea.displayName = 'PromptInputTextarea'
